import { withAuthUser } from 'next-firebase-auth'
import { useRouter } from 'next/router'
import queryString from 'query-string'
// discover new dashboards
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useEffectOnce } from 'react-use'
import Head from 'next/head'
import {
  Box,
  Center,
  Divider,
  HStack,
  Skeleton,
  Spacer,
  Text,
  VStack,
  useMediaQuery,
} from '@chakra-ui/react'

import {
  DashboardSearchResult as DashboardSearchResultType,
  ElasticDashboardSchema,
} from '../components/discover/DashboardCardOld'
import DashboardSearchResult from '../components/discover/DashboardSearchResult'
import NoResultsView from '../components/discover/NoResultsView'
import SearchBar from '../components/discover/SearchBar'
import UserSearchResult from '../components/discover/UserSearchResult'
import {
  UserSearchResultData,
  UserSearchResult as UserSearchResultType,
} from '../components/discover/types'
import PageLayout from '../components/layout/PageLayout'
import { routes } from '../utils/constants'
import firebase from '../utils/firebase/firebase'
import useGAViewLogger from '../utils/hooks/useGAViewLogger'

type SearchResult = DashboardSearchResultType | UserSearchResultType
export type SearchType = 'all' | 'dashboards' | 'users'
export type SortByOption = 'popularity' | 'recency' | 'relevance'

export interface SearchInputState {
  searchInput: string
  searchType: SearchType
  filterTags: string[]
  sortBy: SortByOption
}

interface DiscoverMainData {
  mostPopular: DashboardSearchResultType[]
  newAndPopular: DashboardSearchResultType[]
}

const SearchResultDivider = () => <Divider borderColor={'gray.300'} />

const Discover = () => {
  useGAViewLogger('discover', 'discover')
  const router = useRouter()
  const { asPath: queryPath } = router

  const queryParams = queryString.parse(queryString.extract(queryPath))
  const queryParamSearchInput = (queryParams.q as string | undefined) ?? ''
  const queryParamFilterTagsString =
    (queryParams.filterTags as string | undefined) ?? ''
  const [searchInput, setSearchInput] = useState(queryParamSearchInput)
  const [filterTags, setFilterTags] = useState<string[]>(
    queryParamFilterTagsString === ''
      ? []
      : queryParamFilterTagsString?.split(',') ?? []
  )
  const [sortBy, setSortBy] = useState<SortByOption>('relevance')

  // TODO: @cooper probably a better way to do this kind of conversion
  let queryParamSearchType = (queryParams.type as string | undefined) ?? 'all'
  const allowedTypes: string[] = ['all', 'dashboards', 'users']
  if (!allowedTypes.includes(queryParamSearchType)) queryParamSearchType = 'all'
  const [searchType, setSearchType] = useState<SearchType>(
    queryParamSearchType as SearchType
  )
  const [searchResults, setSearchResults] = useState<
    SearchResult[] | undefined
  >(undefined)
  const [discoverMainDataState, setDiscoverMainDataState] = useState<
    DiscoverMainData | undefined
  >()
  const [isLoading, setIsLoading] = useState(true)

  const {
    q: qFromPath,
    type: typeFromPath,
    filterTags: filterTagsFromPath,
    sortBy: sortByFromPath,
  } = router.query

  const shouldExecuteSearch = !(
    (qFromPath == undefined || qFromPath == '') &&
    (filterTagsFromPath == undefined ||
      filterTagsFromPath == '' ||
      filterTagsFromPath.length == 0)
  )

  useEffectOnce(() => {
    const fetchDiscoverData = firebase
      .functions()
      .httpsCallable('discover-main')
    fetchDiscoverData()
      .then((discoverData) => {
        setDiscoverMainDataState(discoverData.data as DiscoverMainData)
      })
      .finally(() => setIsLoading(false))
  })

  const handleSelectSearchType = (type: SearchType) => {
    setSearchType(type)

    if (shouldExecuteSearch) {
      executeQuery({ searchType: type })
    }
  }

  const cloudFunctionForSearchType = (searchType: SearchType): string => {
    switch (searchType) {
      case 'all':
        return 'queries-searchAll'
      case 'dashboards':
        return 'queries-searchDashboards'
      case 'users':
        return 'queries-searchUsers'
    }
  }

  const saveToQueryParams = (state: SearchInputState) => {
    router.push({
      pathname: '/discover',
      query: {
        q: state.searchInput,
        type: state.searchType,
        filterTags: state.filterTags.join(','),
        sortBy: state.sortBy,
      },
    })
  }

  const executeQuery = (partial?: Partial<SearchInputState>) => {
    setIsLoading(true)
    const state: SearchInputState = {
      searchInput: partial?.searchInput ?? searchInput,
      searchType: partial?.searchType ?? searchType,
      filterTags: partial?.filterTags ?? filterTags,
      sortBy: partial?.sortBy ?? sortBy,
    }

    saveToQueryParams(state)

    // if (state.searchInput === '') {
    //   setSearchResults(undefined)
    //   setIsLoading(false)

    //   return
    // }

    const cloudFunctions = firebase.functions()

    const search = cloudFunctions.httpsCallable(
      cloudFunctionForSearchType(state.searchType)
    )

    search({
      query: state.searchInput != '' ? state.searchInput : undefined,
      filterTags: state.filterTags,
      sortBy: state.sortBy,
    })
      .then(formatSearchResults)
      .catch((reason) => console.warn(reason))
      .finally(() => setIsLoading(false))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      executeQuery()
    }
  }

  useEffect(() => {
    if (!shouldExecuteSearch) {
      setSearchResults(undefined)
    }
  }, [shouldExecuteSearch])

  const formatSearchResults = (
    response: firebase.functions.HttpsCallableResult
  ) => {
    let allResults
    if (response.data.users == undefined) {
      allResults = response.data
    } else {
      allResults = [...response.data.users, ...response.data.dashboards]
    }

    const formattedResults = allResults.map(
      (rawResult: Record<string, unknown>) => {
        if (rawResult._index === 'users') {
          return {
            id: rawResult._id as string,
            type: 'user',
            score: rawResult._score as number,
            data: rawResult._source as UserSearchResultData,
          }
        } else {
          return {
            id: rawResult._id as string,
            type: 'dashboard',
            score: rawResult._score as number,
            data: rawResult._source as ElasticDashboardSchema,
          }
        }
      }
    )

    setSearchResults(formattedResults)
  }

  useEffect(() => {
    if (!shouldExecuteSearch) {
      return
    }

    const queryParamSearchInput = (qFromPath as string | undefined) ?? ''
    const queryParamFilterTagsString =
      (filterTagsFromPath as string | undefined) ?? ''
    const filterTagsFromPathAsArray =
      queryParamFilterTagsString === ''
        ? []
        : queryParamFilterTagsString?.split(',') ?? []
    setSearchInput(queryParamSearchInput)
    setFilterTags(filterTagsFromPathAsArray)
    setSearchType(typeFromPath as SearchType)

    const cloudFunctions = firebase.functions()

    const search = cloudFunctions.httpsCallable(
      cloudFunctionForSearchType(typeFromPath as SearchType)
    )

    if (shouldExecuteSearch) {
      search({
        query: qFromPath,
        filterTags: filterTagsFromPathAsArray,
        sortBy: sortByFromPath,
      })
        .then(formatSearchResults)
        .catch((reason) => console.warn(reason))
        .finally(() => setIsLoading(false))
    }
  }, [
    qFromPath,
    typeFromPath,
    filterTagsFromPath,
    sortByFromPath,
    shouldExecuteSearch,
  ])

  // Initial render
  useEffectOnce(() => {
    if (!shouldExecuteSearch) {
      return
    }
    executeQuery()
  })

  const dashboardCardForElasticData = (data: ElasticDashboardSchema) => {
    const created = new Date(data.created)
    return (
      <DashboardSearchResult
        author={data.author}
        created={created}
        description={data.description}
        followerCount={data.followerCount}
        id={data.id}
        isPublic={true}
        tags={data.tags}
        title={data.title}
        widgetCounts={data.widgetCounts}
        key={data.id}
      />
    )
  }

  const popularDashboardsView = useMemo(
    () => (
      <VStack alignItems="start">
        <Text as="b" fontSize="x-large">
          Popular Dashboards
        </Text>
        <VStack align="stretch" w="100%" spacing={0}>
          {discoverMainDataState?.mostPopular != undefined &&
            discoverMainDataState.mostPopular.length > 0 && (
              <SearchResultDivider />
            )}
          {discoverMainDataState?.mostPopular.map((dashboard) => {
            const { data } = dashboard
            const created = new Date(data.created)
            return (
              <>
                <DashboardSearchResult
                  author={data.author}
                  created={created}
                  description={data.description}
                  followerCount={data.followerCount}
                  id={data.id}
                  isPublic={true}
                  tags={data.tags}
                  title={data.title}
                  widgetCounts={data.widgetCounts}
                  key={data.id}
                />
                <SearchResultDivider />
              </>
            )
          })}
        </VStack>
      </VStack>
    ),
    [discoverMainDataState?.mostPopular]
  )

  const showSearchResults = searchResults != undefined
  const showLoadingIndicator = isLoading

  const handleChangeSearchInput = (input: string) => {
    if (input === '') {
      const state: SearchInputState = {
        searchInput: '',
        searchType,
        filterTags,
        sortBy,
      }
      saveToQueryParams(state)
      setSearchResults(undefined)
    }

    setSearchInput(input)
  }

  const handleTagsChange = (newTags: string[]) => {
    setFilterTags(newTags)
    const state: SearchInputState = {
      searchInput,
      searchType,
      filterTags: newTags,
      sortBy,
    }
    executeQuery(state)
  }

  const handleSortByChange = (option: SortByOption) => {
    setSortBy(option)
  }

  const userSearchResults = searchResults?.filter(
    (result): result is UserSearchResultType => result.type === 'user'
  )
  const dashboardSearchResults = searchResults?.filter(
    (result): result is DashboardSearchResultType => result.type === 'dashboard'
  )

  // useEffect(() => {
  //   executeQuery()
  // }, [filterTags, executeQuery])

  const userSearchResultsDisplay = (
    <VStack alignItems="start" w="100%" pt={0} spacing={0}>
      {userSearchResults != undefined && userSearchResults.length > 0 && (
        <>
          <SearchResultDivider />
          {userSearchResults.map((searchResult) => {
            return (
              <>
                <UserSearchResult key={searchResult.id} result={searchResult} />
                <SearchResultDivider />
              </>
            )
          })}
        </>
      )}
    </VStack>
  )

  const dashboardSearchResultsDisplay = (
    <VStack alignItems="start" w="100%" pt={0} spacing={0}>
      {dashboardSearchResults != undefined &&
        dashboardSearchResults.length > 0 && (
          <>
            <SearchResultDivider />
            {dashboardSearchResults.map((searchResult) => {
              return (
                <>
                  {dashboardCardForElasticData(searchResult.data)}
                  <SearchResultDivider />
                </>
              )
            })}
          </>
        )}
    </VStack>
  )

  const handleSeeMoreResults = (
    e: React.MouseEvent,
    searchType: 'users' | 'dashboards'
  ) => {
    e.preventDefault()

    router.push({
      query: {
        q: searchInput,
        searchType,
      },
    })

    executeQuery()
  }

  const [isMobile] = useMediaQuery('(max-width: 840px)')
  const pageTitle =
    qFromPath ?? '' != ''
      ? `${qFromPath} - Signalist Search`
      : 'Signalist - Popular'

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <Center h="100%" px={0} overflowY="scroll">
        <VStack
          p={2}
          px={isMobile ? 4 : 6}
          align="start"
          w={isMobile ? '100%' : 'min(800px,100%)'}
          h="100%"
        >
          <SearchBar
            onSearch={executeQuery}
            onTagsChange={handleTagsChange}
            searchInput={searchInput}
            searchType={searchType}
            sortBy={sortBy}
            onChangeSortBy={handleSortByChange}
            onKeyDown={handleKeyDown}
            onSearchInputChange={handleChangeSearchInput}
            onSelectSearchType={handleSelectSearchType}
            filterTags={filterTags}
          />
          <Box py={5} w="100%">
            {showLoadingIndicator && (
              <VStack alignItems="start" w="100%" p={0} spacing={4}>
                <Skeleton height="120px" w="100%" />
                <Skeleton height="120px" w="100%" />
                <Skeleton height="120px" w="100%" />
              </VStack>
            )}
            {!showLoadingIndicator &&
              !showSearchResults &&
              popularDashboardsView}
            {!showLoadingIndicator &&
              showSearchResults &&
              searchResults?.length === 0 && <NoResultsView />}
            {!showLoadingIndicator && showSearchResults && (
              <>
                {searchType === 'users' && userSearchResultsDisplay}
                {searchType === 'dashboards' && dashboardSearchResultsDisplay}

                {searchType === 'all' && (
                  <VStack spacing={'5rem'}>
                    {userSearchResults != undefined &&
                      userSearchResults.length > 0 && (
                        <VStack w="100%">
                          <HStack align="center" minW="100%">
                            <Text fontSize={20} fontWeight="600">
                              Users
                            </Text>
                            <Spacer />
                            <Text
                              color="gray.600"
                              _hover={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                              as="a"
                              href={routes.discoverWithParams({
                                searchInput,
                                searchType: 'users',
                                sortBy,
                              })}
                              onClick={(e) => {
                                handleSeeMoreResults(e, 'users')
                              }}
                            >
                              View more
                            </Text>
                          </HStack>
                          {userSearchResultsDisplay}
                        </VStack>
                      )}

                    {dashboardSearchResults != undefined &&
                      dashboardSearchResults.length > 0 && (
                        <VStack w="100%">
                          <HStack align="center" minW="100%">
                            <Text fontSize={20} fontWeight="600">
                              Dashboards
                            </Text>
                            <Spacer />
                            <Text
                              color="gray.600"
                              _hover={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                              as="a"
                              href={routes.discoverWithParams({
                                searchInput,
                                searchType: 'dashboards',
                                filterTags,
                                sortBy,
                              })}
                              onClick={(e) => {
                                handleSeeMoreResults(e, 'dashboards')
                              }}
                            >
                              View more
                            </Text>
                          </HStack>
                          {dashboardSearchResultsDisplay}
                        </VStack>
                      )}
                  </VStack>
                )}
              </>
            )}
          </Box>
        </VStack>
      </Center>
    </>
  )
}

const getLayout = (page: ReactElement): ReactElement => (
  <PageLayout>{page}</PageLayout>
)

Discover.getLayout = getLayout

export default withAuthUser()(Discover)
